'use client';

import React, { useState, useRef, useEffect } from 'react';

interface RangeSliderProps {
  value: number;
  onChange: (value: number) => void;
  minValue?: number;
  maxValue?: number;
  step?: number;
  disabled?: boolean;
  className?: string;
  label?: string;
  valueSuffix?: string;
}

export function RangeSlider({
  value,
  onChange,
  minValue = 0,
  maxValue = 100,
  step = 1,
  disabled = false,
  className = '',
  label,
  valueSuffix = 'x',
}: RangeSliderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);

  const range = maxValue - minValue;
  const percentage = ((value - minValue) / range) * 100;

  const handleMouseDown = (e: React.MouseEvent) => {
    if (disabled) return;
    setIsDragging(true);
    updateValue(e);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || disabled) return;
    updateValue(e);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const updateValue = (e: MouseEvent | React.MouseEvent) => {
    if (!sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const rawValue = minValue + (percentage / 100) * range;

    // Round to nearest step
    const steppedValue = Math.round(rawValue / step) * step;

    // Clamp between minValue and maxValue
    const clampedValue = Math.max(minValue, Math.min(maxValue, steppedValue));

    onChange(clampedValue);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  return (
    <div className={`space-y-3 ${className}`}>
      {label && <div className="text-sm font-medium text-neutral-300">{label}</div>}

      {/* Slider Container */}
      <div className="relative">
        {/* Slider Track */}
        <div
          ref={sliderRef}
          className={`relative h-1 bg-neutral-700 rounded-full cursor-pointer ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          onMouseDown={handleMouseDown}
        >
          {/* Active Track */}
          <div
            className="absolute top-0 left-0 h-full bg-primary rounded-full transition-all duration-150"
            style={{ width: `${percentage}%` }}
          />

          {/* Handle */}
          <div
            className={`absolute top-1/2 w-4 h-4 bg-primary rounded-full transform -translate-y-1/2 cursor-grab transition-all duration-150 ${
              isDragging ? 'cursor-grabbing scale-110' : 'hover:scale-105'
            } ${disabled ? 'cursor-not-allowed' : ''}`}
            style={{ left: `${percentage}%` }}
          />
        </div>
      </div>
      {/* Value Display */}
      <div className="flex justify-between items-center mt-3 text-neutral-100 font-medium text-sm">
        <div>{`${minValue}${valueSuffix}`}</div>
        <div>{`${maxValue}${valueSuffix}`}</div>
      </div>
    </div>
  );
}
